/**
 * `aiRealtimeMarkdownFrontmatter.ts` — port of Unity's `AI/AIRealtimeMarkdownFrontmatter.cs`.
 *
 * Shared parser for optional leading YAML frontmatter in the markdown documents Spark loads at
 * runtime (skills, roles, personalities). Frontmatter is delimited by lines containing only "---".
 *
 * Field keys are stored TRIMMED + LOWERCASED to emulate the Unity dictionary's
 * `StringComparer.OrdinalIgnoreCase` — all catalog lookups use lowercase keys.
 */

export interface FrontmatterParseResult {
  /** True when a valid frontmatter block was found and parsed. */
  readonly found: boolean;
  /** Parsed key/value fields, keyed by trimmed lowercase key. */
  readonly fields: Readonly<Record<string, string>>;
  /** The markdown body with the frontmatter stripped (trimmed). */
  readonly body: string;
}

/**
 * Parses an optional leading YAML frontmatter block delimited by lines containing only "---".
 * Mirrors `AIRealtimeMarkdownFrontmatter.TryParse`.
 */
export function tryParseFrontmatter(markdown: string | null | undefined): FrontmatterParseResult {
  const fallbackBody = (markdown ?? "").trim();
  const region = findFrontmatterRegion(markdown);
  if (!region) {
    return { found: false, fields: {}, body: fallbackBody };
  }

  const { lines, start, closing } = region;
  const fields: Record<string, string> = {};
  for (let i = start + 1; i < closing; i++) {
    const line = lines[i];
    const colonIndex = line.indexOf(":");
    if (colonIndex <= 0) {
      continue;
    }

    const key = line.substring(0, colonIndex).trim();
    let value = line.substring(colonIndex + 1).trim();
    if (
      value.length >= 2 &&
      ((value[0] === '"' && value[value.length - 1] === '"') ||
        (value[0] === "'" && value[value.length - 1] === "'"))
    ) {
      value = value.substring(1, value.length - 1);
    }

    if (key.length > 0) {
      fields[key.toLowerCase()] = value;
    }
  }

  const body = lines
    .slice(closing + 1)
    .join("\n")
    .trim();
  return { found: true, fields, body };
}

/** The `---`-fenced region of a document, in split lines. Null when there is none. */
export interface FrontmatterRegion {
  readonly lines: readonly string[];
  /** Index of the opening `---`. */
  readonly start: number;
  /** Index of the closing `---`. */
  readonly closing: number;
}

/**
 * Locates the frontmatter fence. Split out of {@link tryParseFrontmatter} so the
 * override-divergence check can scan the same region for shapes the field parser
 * silently drops (a YAML block list) without re-deriving where frontmatter ends —
 * two answers to "where is the frontmatter" is exactly how such a check goes stale.
 */
export function findFrontmatterRegion(
  markdown: string | null | undefined,
): FrontmatterRegion | null {
  if (!markdown || !markdown.trim()) {
    return null;
  }

  const lines = markdown.split(/\r\n|\n/);
  let start = 0;
  while (start < lines.length && lines[start].trim().length === 0) {
    start++;
  }

  if (start >= lines.length || lines[start].trim() !== "---") {
    return null;
  }

  for (let i = start + 1; i < lines.length; i++) {
    if (lines[i].trim() === "---") {
      return { lines, start, closing: i };
    }
  }

  return null;
}

/**
 * Splits a comma/semicolon/pipe/slash-separated frontmatter value into trimmed, non-empty tokens.
 * Mirrors `AIRealtimeMarkdownFrontmatter.SplitList`.
 */
export function splitList(rawValue: string | null | undefined): string[] {
  if (!rawValue || !rawValue.trim()) {
    return [];
  }

  return rawValue
    .split(/[,;|/]/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
}

/**
 * Trims and de-duplicates (case-insensitively) a token list, preserving original casing and order.
 * Mirrors `AIRealtimeMarkdownFrontmatter.NormalizeTokens`.
 */
export function normalizeTokens(tokens: readonly string[] | null | undefined): string[] {
  if (!tokens || tokens.length === 0) {
    return [];
  }

  const normalized: string[] = [];
  const seen = new Set<string>();
  for (const token of tokens) {
    if (!token || !token.trim()) {
      continue;
    }

    const trimmed = token.trim();
    const key = trimmed.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      normalized.push(trimmed);
    }
  }

  return normalized;
}
